// Increment 44: client-side meta/OG tag management for public pages. Sets the
// document title and upserts standard SEO + Open Graph + Twitter tags so shared
// links and detail pages carry accurate, per-listing metadata.
export interface PageMeta {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(url: string) {
  if (!url) return;
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", url);
}

const DEFAULT_TITLE = "KontrataPH — Trabaho. Serbisyo. Negosyo. Kontrata.";
const DEFAULT_DESC = "The open Filipino marketplace for work, services, projects, and contracts.";

/** Apply page metadata (title + SEO/OG/Twitter tags). */
export function setMeta(meta: PageMeta) {
  const title = meta.title || DEFAULT_TITLE;
  const description = meta.description || DEFAULT_DESC;
  const url = meta.url || (typeof window !== "undefined" ? window.location.href : "");
  const image = meta.image || "";
  const type = meta.type || "website";

  document.title = title;
  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:type", type);
  if (url) upsertMeta("property", "og:url", url);
  if (image) upsertMeta("property", "og:image", image);
  upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  if (image) upsertMeta("name", "twitter:image", image);
  if (url) upsertCanonical(url);
}

/** Reset to the site defaults (call on unmount of a detail page). */
export function resetMeta() {
  setMeta({ title: DEFAULT_TITLE, description: DEFAULT_DESC });
}
